import React from 'react';
import { Platform, RegisteredStyle, View, ViewStyle } from 'react-native';
import { captureRef } from 'react-native-view-shot';

import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ButtonVariant } from '../../../components/CustomButton';
import { TrackedButton } from '../../../analytics/components';

export interface ShareButtonProps {
    viewRef: React.LegacyRef<View>;
    title?: string;
    summary?: string;
    comment?:string;
    deepLink: string;
    style?: RegisteredStyle<ViewStyle>;
}

const ShareButton = ({ ...props }: ShareButtonProps) => {
    const { t } = useTranslation();
    const separator = props.deepLink.includes('?') ? '&' : '?';
    const shareUrl = `${process.env.EXPO_PUBLIC_WEB_URL}/${props.deepLink}${separator}src=share`;
    const onShare = useCallback(async () => {
        try {
            const deepLink = shareUrl;
            if (Platform.OS === 'web') {
                // ✅ Web Share API if supported
                if (navigator.share) {
                    await navigator.share({
                        text: `${props?.title}\n ${props.comment}\n`,
                        url: deepLink,
                    });
                } else {
                    // ✅ Fallback: copy link to clipboard
                    await navigator.clipboard.writeText(deepLink);
                    alert("🔗 Link copied to clipboard!");
                }
                return;
            } else {
                const Share = require('react-native-share').default;

                // Wait 1 frame to ensure React 18 + Fabric fully commits the view
                await new Promise(res => setTimeout(res, 100));

                const uri = await captureRef(props?.viewRef as React.RefObject<View>, {
                    format: "png",
                    quality: 1,
                    result: "tmpfile", // stores to temp file
                });

                await Share.open({
                    title: props.title,
                    message: `${props.title}\n\n🔗 ${deepLink}\n\n ${t('screens.share.via')}`,
                    url: Platform.OS === 'android' ? `file://${uri}` : uri,
                });
            }
        } catch (error: any) {
            if (error?.message !== 'User did not share') {
                console.log("Snapshot failed", error);
            }
        }
    }, [props?.comment]);
    return (
        <TrackedButton context={{ cta: 'SHARE', deepLink: shareUrl }}
            variant={ButtonVariant.NORMAL_PRIMARY} style={props.style} icon='share' onPress={onShare}>
            {t('screens.share.shareButton')}
        </TrackedButton>
    );
};

export default ShareButton;